import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    bulkAssignUsersRequest,
    fetchAssignableUsersRequest,
    fetchAssignedUsersByEventRequest,
    fetchAssignedUsersByModuleRequest,
    removeAssignmentRequest,
} from "../../../../../redux/actions/assignmentActions";
import {
    Calendar,
    Mail,
    User,
    Briefcase,
    Building2,
    Trash2,
    ChevronRight,
    Search,
    Users,
    AlertCircle
} from "lucide-react";
import "../../RessourcesStyles/AssignmentManagement.css";

const PAGE_SIZE = 10;

const norm = (value) => String(value || "").trim().toLowerCase();
const buildContextKey = (assignmentType, targetId) => `${assignmentType}:${targetId}`;

export default function BulkAssignmentModal({
    open,
    onClose,
    assignmentType,
    targetId,
    targetLabel,
    assignmentDisabled = false,
    assignmentDisabledMessage = "",
}) {
    const dispatch = useDispatch();
    const assignmentsState = useSelector((state) => state.assignments || {});

    const rawAssignableUsers = assignmentsState.assignableUsers;
    const assignableUsers = useMemo(() => rawAssignableUsers || [], [rawAssignableUsers]);
    const rawAssignedUsers = assignmentType === "EVENT"
        ? assignmentsState.assignedUsersByEvent?.[targetId]
        : assignmentsState.assignedUsersByModule?.[targetId];
    const assignedUsers = useMemo(() => rawAssignedUsers || [], [rawAssignedUsers]);
    const loading = !!assignmentsState.loading;
    const submitting = !!assignmentsState.submitting;
    const removing = !!assignmentsState.removing;
    const bulkResult = assignmentsState.bulkResult;
    const bulkResultContextKey = assignmentsState.bulkResultContextKey;

    const [search, setSearch] = useState("");
    const [selectedPoste, setSelectedPoste] = useState("");
    const [selectedEntite, setSelectedEntite] = useState("");
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [page, setPage] = useState(1);
    const [activeTab, setActiveTab] = useState("ASSIGN"); // ASSIGN | ASSIGNED

    const assignedUserIds = useMemo(
        () => new Set((assignedUsers || []).map((user) => user.userId || user.id)),
        [assignedUsers]
    );

    useEffect(() => {
        if (!open || !targetId || !assignmentType) return;

        dispatch(fetchAssignableUsersRequest());
        if (assignmentType === "EVENT") dispatch(fetchAssignedUsersByEventRequest(targetId));
        if (assignmentType === "MODULE") dispatch(fetchAssignedUsersByModuleRequest(targetId));

        setSearch("");
        setSelectedPoste("");
        setSelectedEntite("");
        setSelectedIds(new Set());
        setPage(1);
        setActiveTab("ASSIGN");
    }, [dispatch, open, targetId, assignmentType]);

    const availablePostes = useMemo(() => {
        const set = new Set();
        assignableUsers.forEach(u => { if (u.jobName) set.add(u.jobName); });
        return Array.from(set).sort();
    }, [assignableUsers]);

    const availableEntites = useMemo(() => {
        const set = new Set();
        assignableUsers.forEach(u => { if (u.entityName) set.add(u.entityName); });
        return Array.from(set).sort();
    }, [assignableUsers]);

    const filteredUsers = useMemo(() => {
        const query = norm(search);
        return assignableUsers.filter((user) => {
            const matchesSearch = !query || (
                norm(user.fullName).includes(query) ||
                norm(`${user.firstName || ""} ${user.lastName || ""}`).includes(query) ||
                norm(user.email).includes(query) ||
                norm(user.employeeCode).includes(query) ||
                norm(user.jobName || user.jobTitle || user.poste).includes(query) ||
                norm(user.entityName || user.departement).includes(query)
            );
            
            const userPoste = user.jobName || user.jobTitle || user.poste || "";
            const userEntite = user.entityName || user.departement || "";
            
            const matchesPoste = !selectedPoste || userPoste === selectedPoste;
            const matchesEntite = !selectedEntite || userEntite === selectedEntite;

            return matchesSearch && matchesPoste && matchesEntite;
        });
    }, [assignableUsers, search, selectedPoste, selectedEntite]);

    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const startIndex = (safePage - 1) * PAGE_SIZE;
    const pageItems = filteredUsers.slice(startIndex, startIndex + PAGE_SIZE);
    const pageSelectableIds = pageItems
        .filter((user) => !assignedUserIds.has(user.id))
        .map((user) => user.id);
    const allPageSelected = pageSelectableIds.length > 0 && pageSelectableIds.every((id) => selectedIds.has(id));
    const somePageSelected = pageSelectableIds.some((id) => selectedIds.has(id));

    const toggleUser = (userId) => {
        if (assignedUserIds.has(userId)) return;

        setSelectedIds((previous) => {
            const next = new Set(previous);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    };

    const togglePageSelection = () => {
        setSelectedIds((previous) => {
            const next = new Set(previous);
            if (allPageSelected) {
                pageSelectableIds.forEach((id) => next.delete(id));
            } else {
                pageSelectableIds.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    const handleSubmit = () => {
        if (assignmentDisabled || !selectedIds.size) return;

        dispatch(
            bulkAssignUsersRequest({
                assignmentType,
                eventId: assignmentType === "EVENT" ? targetId : null,
                moduleId: assignmentType === "MODULE" ? targetId : null,
                userIds: Array.from(selectedIds),
            })
        );
    };

    const handleRemove = (assignmentId) => {
        dispatch(
            removeAssignmentRequest(assignmentId, {
                assignmentType,
                targetId,
            })
        );
    };

    if (!open) return null;

    return (
        <div className="assignmentModalOverlay" onMouseDown={onClose}>
            <div className="assignmentModalCard" onMouseDown={(event) => event.stopPropagation()}>
                <div className="assignmentModalHeader">
                    <div>
                        <h3>Affectation massive</h3>
                        <p>
                            {assignmentType === "EVENT" ? "Événement" : "Module"} :
                            <strong> {targetLabel || `#${targetId}`}</strong>
                        </p>
                    </div>
                    <button className="assignmentBtn assignmentBtnLight" type="button" onClick={onClose}>
                        Fermer
                    </button>
                </div>

                <div className="assignmentTabs">
                    <button
                        className={`assignmentTab ${activeTab === "ASSIGN" ? "active" : ""}`}
                        onClick={() => setActiveTab("ASSIGN")}
                    >
                        <UserPlus size={18} />
                        <span>Affecter</span>
                    </button>
                    <button
                        className={`assignmentTab ${activeTab === "ASSIGNED" ? "active" : ""}`}
                        onClick={() => setActiveTab("ASSIGNED")}
                    >
                        <Users size={18} />
                        <span>Déjà affectés</span>
                        <span className="tabCounter">{assignedUsers.length}</span>
                    </button>
                </div>

                <div className="assignmentBody">
                    {activeTab === "ASSIGN" ? (
                        <>
                            {assignmentDisabled && (
                                <div className="assignmentFeedbackRow">
                                    <AlertCircle size={16} />
                                    <span>{assignmentDisabledMessage}</span>
                                </div>
                            )}
                            <div className="assignmentToolbar">
                                <div className="assignmentField">
                                    <label>Recherche utilisateur</label>
                                    <div className="assignmentSearchInputWrapper">
                                        <input
                                            value={search}
                                            onChange={(event) => {
                                                setSearch(event.target.value);
                                                setPage(1);
                                            }}
                                            placeholder="Rechercher par nom, email, matricule..."
                                        />
                                        <Search className="assignmentSearchIcon" size={18} />
                                    </div>
                                </div>
                                <div className="assignmentField">
                                    <label>Poste</label>
                                    <select 
                                        className="assignmentInputReadOnly"
                                        style={{ width: "100%", background: "#fff", cursor: "pointer" }}
                                        value={selectedPoste}
                                        onChange={(e) => { setSelectedPoste(e.target.value); setPage(1); }}
                                    >
                                        <option value="">Tous les postes</option>
                                        {availablePostes.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                                <div className="assignmentField">
                                    <label>Entité</label>
                                    <select 
                                        className="assignmentInputReadOnly"
                                        style={{ width: "100%", background: "#fff", cursor: "pointer" }}
                                        value={selectedEntite}
                                        onChange={(e) => { setSelectedEntite(e.target.value); setPage(1); }}
                                    >
                                        <option value="">Toutes les entités</option>
                                        {availableEntites.map(e => <option key={e} value={e}>{e}</option>)}
                                    </select>
                                </div>
                                <div className="assignmentField">
                                    <button 
                                        className="assignmentBtnGhost"
                                        style={{ height: 36, display: 'flex', alignItems: 'center' }}
                                        type="button"
                                        onClick={() => {
                                            setSearch("");
                                            setSelectedPoste("");
                                            setSelectedEntite("");
                                            setPage(1);
                                        }}
                                        disabled={!search && !selectedPoste && !selectedEntite}
                                    >
                                        Effacer
                                    </button>
                                </div>
                            </div>

                            <div className="assignmentActionsRow">
                                <div className="bulkSelectionActions">
                                    <button
                                        className="assignmentBtn assignmentBtnGhost"
                                        type="button"
                                        onClick={togglePageSelection}
                                        disabled={assignmentDisabled || !pageSelectableIds.length}
                                    >
                                        {allPageSelected ? "Désélectionner la page" : "Sélectionner la page"}
                                    </button>
                                    <button
                                        className="assignmentBtn assignmentBtnGhost"
                                        type="button"
                                        onClick={() => {
                                            setSearch("");
                                            setSelectedPoste("");
                                            setSelectedEntite("");
                                            setSelectedIds(new Set());
                                        }}
                                        disabled={assignmentDisabled || (!selectedIds.size && !search && !selectedPoste && !selectedEntite)}
                                    >
                                        Réinitialiser
                                    </button>
                                </div>
                                {loading && <div className="assignmentLoadingSpinner">Chargement des données...</div>}
                            </div>

                            <div className="assignmentTableWrap">
                                <table className="assignmentTable">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 46 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={allPageSelected}
                                                    disabled={assignmentDisabled || pageSelectableIds.length === 0}
                                                    ref={(element) => {
                                                        if (element) element.indeterminate = !allPageSelected && somePageSelected;
                                                    }}
                                                    onChange={togglePageSelection}
                                                />
                                            </th>
                                            <th>Matricule</th>
                                            <th>Collaborateur</th>
                                            <th>Poste</th>
                                            <th>Entité</th>
                                            <th style={{ textAlign: "center" }}>Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageItems.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="assignmentEmptyCell">
                                                    {loading ? "Recherche en cours..." : "Aucun collaborateur trouvé"}
                                                </td>
                                            </tr>
                                        ) : (
                                            pageItems.map((user) => {
                                                const alreadyAssigned = assignedUserIds.has(user.id);
                                                const checked = selectedIds.has(user.id);

                                                return (
                                                    <tr
                                                        key={user.id}
                                                        className={`${alreadyAssigned ? "assignmentRowDisabled" : ""} ${checked ? "assignmentRowSelected" : ""}`}
                                                        onClick={() => !assignmentDisabled && !alreadyAssigned && toggleUser(user.id)}
                                                    >
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                disabled={assignmentDisabled || alreadyAssigned}
                                                                checked={checked}
                                                                onChange={(e) => e.stopPropagation()}
                                                                readOnly
                                                            />
                                                        </td>
                                                        <td>
                                                            <div className="userSecondaryInfo">
                                                                <span className="userCodeText">{user.employeeCode || ""}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="userMainInfo">
                                                                <span className="userNameText">{user.fullName || `${user.firstName || ""} ${user.lastName || ""}`.trim() || `User #${user.id}`}</span>
                                                            </div>
                                                        </td>

                                                        <td>
                                                            <div className="userJobText">{user.jobName || "—"}</div>
                                                        </td>
                                                        <td>
                                                            <div className="userDeptText">{user.entityName || "—"}</div>
                                                        </td>
                                                        <td style={{ textAlign: "center" }}>
                                                            <span className={`assignmentStatusPill ${alreadyAssigned ? "isAssigned" : "isAvailable"}`}>
                                                                {alreadyAssigned ? "Déjà affecté" : "Disponible"}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="assignmentPagination">
                                <button
                                    className="assignmentBtn assignmentBtnNav"
                                    type="button"
                                    onClick={() => setPage((previous) => Math.max(1, previous - 1))}
                                    disabled={safePage <= 1}
                                >
                                    Précédent
                                </button>
                                <span className="assignmentPageIndicator">Page <strong>{safePage}</strong> sur {totalPages}</span>
                                <button
                                    className="assignmentBtn assignmentBtnNav"
                                    type="button"
                                    onClick={() => setPage((previous) => Math.min(totalPages, previous + 1))}
                                    disabled={safePage >= totalPages}
                                >
                                    Suivant
                                </button>
                            </div>

                            {bulkResult && bulkResultContextKey === buildContextKey(assignmentType, targetId) && (
                                <div className="assignmentFeedbackRow">
                                    <AlertCircle size={16} />
                                    <span>
                                        <strong>{bulkResult.totalCreated || 0}</strong> affecté(s) avec succès,
                                        <strong> {bulkResult.totalSkipped || 0}</strong> déjà présent(s).
                                    </span>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="assignedHeaderInfo">
                                <div className="assignedCountTitle">
                                    <Users size={18} />
                                    <h4>Liste des collaborateurs inscrits</h4>
                                </div>
                                {/*<span className="assignedCountBadge">{assignedUsers.length} total</span>*/}
                            </div>

                            <div className="assignmentTableWrap">
                                <table className="assignmentTable">
                                    <thead>
                                        <tr>
                                            {/*<th><span className="assignmentThContent"> Matricule</span></th>*/}
                                            <th><span className="assignmentThContent"> Nom complet</span></th>
                                            {/*<th><span className="assignmentThContent"><Mail size={14} /> Email</span></th>*/}
                                            <th><span className="assignmentThContent"> Poste</span></th>
                                            <th><span className="assignmentThContent">Département</span></th>
                                            {/*<th><span className="assignmentThContent"> Date</span></th>*/}
                                            <th style={{ width: 140, textAlign: "right" }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {assignedUsers.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="assignmentEmptyCell">
                                                    Aucun collaborateur n'est actuellement affecté.
                                                </td>
                                            </tr>
                                        ) : (
                                            assignedUsers.map((user) => (
                                                <tr key={user.assignmentId}>
                                                    {/*<td>{user.employeeCode || "—"}</td>*/}
                                                    <td>{user.fullName || "—"}</td>
                                                    {/*<td>{user.email || "—"}</td>*/}
                                                    <td>{user.poste || "—"}</td>
                                                    <td>{user.departement || "—"}</td>
                                                    {/*<td>{user.assignedAt ? new Date(user.assignedAt).toLocaleDateString() : "—"}</td>*/}
                                                    <td style={{ textAlign: "right" }}>
                                                        <button
                                                            className="assignmentBtn assignmentBtnRemove"
                                                            type="button"
                                                            onClick={() => handleRemove(user.assignmentId)}
                                                            disabled={removing}
                                                            title="Supprimer l'affectation"
                                                        >
                                                            <Trash2 size={14} />
                                                            Retirer
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>

                <div className="assignmentModalFooter">
                    {activeTab === "ASSIGN" && selectedIds.size > 0 && (
                        <div className="selectionCountHighlight" style={{ marginRight: "auto" }}>
                            {selectedIds.size} sélectionné(s)
                        </div>
                    )}
                    <button className="assignmentBtn assignmentBtnLight" type="button" onClick={onClose}>
                        Annuler
                    </button>
                    {activeTab === "ASSIGN" && (
                        <button
                            className="assignmentBtn assignmentBtnPrimary"
                            type="button"
                            onClick={handleSubmit}
                            disabled={assignmentDisabled || !selectedIds.size || submitting}
                        >
                            {submitting ? "Traitement..." : `Confirmer l'affectation ${selectedIds.size ? `(${selectedIds.size})` : ""}`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// Styles additionnels importés pour Lucide
const UserPlus = (props) => (
    <svg {...props} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <line x1="19" y1="8" x2="19" y2="14" />
        <line x1="22" y1="11" x2="16" y2="11" />
    </svg>

);
